// Professional Earnings Overview Component
import React from 'react';
import { COLORS, LAYOUT } from '../../constants';

const EarningsOverview = ({ 
  availableBalance = 0, 
  pendingBalance = 0, 
  totalSales = 0, 
  activeListings = 0
}) => {

  const statsData = [
    {
      label: 'Available Balance',
      value: `$${availableBalance.toFixed(2)}`,
      icon: (
        <svg fill="currentColor" viewBox="0 0 24 24">
          <path d="M11.8,10.9C9.53,10.31 8.8,9.7 8.8,8.75C8.8,7.66 9.81,6.9 11.5,6.9C13.28,6.9 13.94,7.75 14,9H16.21C16.14,7.28 15.09,5.7 13,5.19V3H10V5.16C8.06,5.58 6.5,6.84 6.5,8.77C6.5,11.08 8.41,12.23 11.2,12.9C13.7,13.5 14.2,14.38 14.2,15.31C14.2,16 13.71,17.1 11.5,17.1C9.44,17.1 8.63,16.18 8.5,15H6.32C6.44,17.19 8.08,18.42 10,18.83V21H13V18.85C14.95,18.5 16.5,17.35 16.5,15.3C16.5,12.46 14.07,11.5 11.8,10.9Z"/>
        </svg>
      ),
      bgColor: COLORS.primary,
      description: 'Auto-deposited daily',
    },
    {
      label: 'Pending Balance',
      value: `$${pendingBalance.toFixed(2)}`,
      icon: (
        <svg fill="currentColor" viewBox="0 0 24 24">
          <path d="M12,20A8,8 0 0,0 20,12A8,8 0 0,0 12,4A8,8 0 0,0 4,12A8,8 0 0,0 12,20M12,2A10,10 0 0,1 22,12A10,10 0 0,1 12,22C6.47,22 2,17.5 2,12A10,10 0 0,1 12,2M12.5,7V12.25L17,14.92L16.25,16.15L11,13V7H12.5Z"/>
        </svg>
      ),
      bgColor: COLORS.primary,
      description: 'Processing payments',
    },
    {
      label: 'Total Sales',
      value: `$${totalSales.toFixed(2)}`,
      icon: (
        <svg fill="currentColor" viewBox="0 0 24 24">
          <path d="M16,6L18.29,8.29L13.41,13.17L9.41,9.17L2,16.59L3.41,18L9.41,12L13.41,16L19.71,9.71L22,12V6H16Z"/>
        </svg>
      ),
      bgColor: COLORS.primary,
      description: 'All time earnings',
    },
    {
      label: 'Active Listings',
      value: activeListings.toString(),
      icon: (
        <svg fill="currentColor" viewBox="0 0 24 24">
          <path d="M12,2A2,2 0 0,1 14,4V6H20A2,2 0 0,1 22,8V19A2,2 0 0,1 20,21H4A2,2 0 0,1 2,19V8C2,6.89 2.9,6 4,6H10V4C10,2.89 10.9,2 12,2M14,8V19H20V8H14M4,8V19H12V8H4Z"/>
        </svg>
      ),
      bgColor: COLORS.primary,
      description: 'Items for sale',
    },
  ];

  return (
    <div style={modernStyles.section} className="earnings-overview">
      <div style={modernStyles.sectionHeader}>
        <h2 style={modernStyles.sectionTitle}>Financial Overview</h2>
        <p style={modernStyles.sectionSubtitle}>
          Track your earnings and monitor business performance
        </p>
      </div>
      
      <div style={modernStyles.statsGrid}>
        {statsData.map((stat, index) => (
          <div 
            key={index} 
            style={modernStyles.statCard}
            className="dashboard-card"
          >
            <div style={modernStyles.cardHeader}>
              <div style={{
                ...modernStyles.iconContainer,
                backgroundColor: `${stat.bgColor}15`,
                color: stat.bgColor,
              }}>
                {stat.icon}
              </div>
            </div>
            
            <div style={modernStyles.statContent}>
              <div style={modernStyles.statValue}>{stat.value}</div>
              <div style={modernStyles.statLabel}>{stat.label}</div>
              <div style={modernStyles.statDescription}>{stat.description}</div>
            </div>
            

          </div>
        ))}
      </div>
    </div>
  );
};

const modernStyles = {
  section: {
    marginBottom: LAYOUT.spacing.xl,
  },
  
  sectionHeader: {
    marginBottom: LAYOUT.spacing.lg,
  },
  
  sectionTitle: {
    fontSize: '1.8rem',
    fontWeight: '700',
    color: COLORS.text.primary,
    marginBottom: LAYOUT.spacing.xs,
  },
  
  sectionSubtitle: {
    fontSize: '0.95rem',
    color: COLORS.text.secondary,
    lineHeight: '1.5',
  },
  
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: LAYOUT.spacing.lg,
  },
  
  statCard: {
    backgroundColor: COLORS.white,
    borderRadius: LAYOUT.borderRadius.xl,
    padding: LAYOUT.spacing.xl,
    border: `1px solid ${COLORS.border}`,
    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
  },
  
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: LAYOUT.spacing.lg,
  },
  
  iconContainer: {
    width: '48px',
    height: '48px',
    borderRadius: LAYOUT.borderRadius.lg,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  trendBadge: {
    padding: `${LAYOUT.spacing.xs} ${LAYOUT.spacing.sm}`,
    borderRadius: LAYOUT.borderRadius.full,
    fontSize: '0.8rem',
    fontWeight: '600',
    border: '1px solid currentColor',
  },
  
  statContent: {
    marginBottom: LAYOUT.spacing.md,
  },
  
  statValue: {
    fontSize: '2.2rem',
    fontWeight: '800',
    color: COLORS.text.primary,
    lineHeight: '1.1',
    marginBottom: LAYOUT.spacing.xs,
  },
  
  statLabel: {
    fontSize: '0.95rem',
    fontWeight: '600',
    color: COLORS.text.secondary,
    marginBottom: '2px',
  },
  
  statDescription: {
    fontSize: '0.8rem',
    color: COLORS.text.muted,
    lineHeight: '1.4',
  },
};

export default EarningsOverview;